import { yupResolver } from "@hookform/resolvers/yup";
import React, { useEffect, useState } from "react";
import { Col, Form } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { useAppSelector } from "../../../../app/hook";
import { selectUseId } from "../../../../features/auth/auth.slice";
import CopyVariable from "../../../common/CopyVariable";
import DeleteButton from "../DeleteButton";
import NodeParentChanger from "../NodeParentChanger";
import { INoteForm } from "../NoteForm";
import ScenarioSide from "../scenario/scenario-side";
import ScenarioHeader from "../ScenarioHeader";
import { MacBotActionKind } from "../types";

export interface IFormData extends INoteForm {
  title: string;
  variables?: null | object;
}

export const UserCredentialForm = ({
  setShow,
  itemData,
  parentId,
  tree,
  loading,
  createScenarioBlock,
  deleteScenarioBlock,
  changeScenarioBlock,
  config
}: {
  setShow: (item: boolean) => void;
  itemData: any;
  loading: boolean;
  createScenarioBlock: (block: any) => Promise<any>;
  deleteScenarioBlock: (block: any) => void;
  changeScenarioBlock: (block: any) => Promise<any>;
  parentId: string | null;
  config: any;
  tree: any[];
}) => {
  const { t } = useTranslation();
  const [isDelete, setIsDelete] = useState(true);
  const userId = useAppSelector(selectUseId);

  const variableFields = [
    { key: "USER_EMAIL", value: "$:SUM_user_email" },
    { key: "USER_IDENTIFICATION", value: "$:SUM_user_identification" },
    { key: "USER_ROLE", value: "$:SUM_user_role" },
    { key: "USER_TYPE", value: "$:SUM_user_type" },
    { key: "USER_LAST_CONNECTION", value: "$:SUM_user_last_connection" },
    { key: "USER_IS_ACTIVE", value: "$:SUM_user_is_active" },
    { key: "USER_ACCOUNT_STATUS", value: "$:SUM_user_account_status" }
  ];

  const schema = Yup.object({
    title: Yup.string()
      .max(30, t("SuperUserManager/blockTitleMaxLength"))
      .required(t("SuperUserManager/blockTitleRequired")),
    variables: Yup.object()
      .shape(
        variableFields.reduce((acc, field) => {
          acc[field.key] = Yup.string().nullable().optional();
          return acc;
        }, {})
      )
      .optional()
      .nullable()
  });
  const {
    register,
    formState: { errors },
    reset,
    setValue,
    handleSubmit,
    control,
    getValues,
    watch
  } = useForm<IFormData>({
    resolver: yupResolver(schema) as any,
    defaultValues: {
      title: "User Credential",
      variables: null
    }
  });

  const { fields, append } = useFieldArray({
    control,
    name: "notes"
  });

  const deleteHandler = () => {
    deleteScenarioBlock(itemData);
    setShow(false);
  };

  const onSubmit = () => {};

  useEffect(() => {
    if (!itemData) {
      setIsDelete(false);
    } else {
      reset({
        title: itemData?.title ?? "",
        variables: itemData?.variables ?? null,
        notes: itemData?.notes
      });
    }
  }, [itemData]);

  return (
    <React.Fragment>
      <ScenarioSide onSubmit={handleSubmit(onSubmit)} size="md">
        <ScenarioHeader
          type={MacBotActionKind.ShowUserCredential}
          onClose={() => {
            reset();
            setShow?.(false);
          }}
        />
        <div className="md:tw-p-6 tw-p-4 tw-bg-gray-50 tw-flex tw-flex-col tw-gap-y-4 tw-w-full">
          <Col xs={12}>
            <Form.Group>
              <Form.Label
                htmlFor=""
                className="tw-text-[#687076] tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none"
              >
                {t("SuperUserManager/blockTitle")}
              </Form.Label>
              <Form.Control
                disabled
                maxLength={31}
                max={31}
                className="tw-self-stretch tw-px-4 tw-py-3 tw-bg-white tw-rounded-md tw-shadow tw-border tw-border-gray-300"
                isInvalid={!!errors.title}
                {...register(`title`)}
              />
              <Form.Control.Feedback
                type="invalid"
                className="d-block font-poppins"
              >
                {errors.title?.message}
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <div className="tw-h-px tw-border tw-border-solid tw-border-gray-200"></div>

          {variableFields.map((item, index) => (
            <React.Fragment key={index.toString()}>
              <Col xs={12} key={item?.key}>
                <Form.Group>
                  <CopyVariable text={item.value?.toString()} ignoreLowerCase />
                </Form.Group>
              </Col>
              <div className="tw-h-px tw-border tw-border-solid tw-border-gray-200"></div>
            </React.Fragment>
          ))}
        </div>
        <div className="tw-flex flex-row tw-justify-between tw-items-center">
          <div className="tw-flex tw-justify-start tw-items-center tw-gap-2">
            {isDelete && <DeleteButton deleteHandler={deleteHandler} />}

            {itemData?.id && (
              <NodeParentChanger tree={tree} currentId={itemData.id} />
            )}
          </div>
          <div className="tw-flex tw-justify-end"></div>
        </div>
      </ScenarioSide>
    </React.Fragment>
  );
};
